import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, FormHelperText, MenuItem } from '@mui/material';

export default function ProfileEdit({ user, onSave, onCancel }) {
    const [draft, setDraft] = useState(user);
    const [errors, setErrors] = useState({});

    const validateDateOfBirthValue = (dateValue) => {
        if (!dateValue || dateValue.trim() === '') {
            return 'Date of birth is required';
        }

        const birthDate = new Date(dateValue);
        const today = new Date();

        if (isNaN(birthDate.getTime())) {
            return 'Please enter a valid date';
        }
        if (birthDate > today) {
            return 'Date of birth cannot be in the future';
        }

        const minDate = new Date();
        minDate.setFullYear(today.getFullYear() - 16);
        if (birthDate > minDate) {
            return 'You must be at least 16 years old.';
        }

        const maxDate = new Date();
        maxDate.setFullYear(today.getFullYear() - 120);
        if (birthDate < maxDate) {
            return 'Please enter a valid date of birth';
        }

        return null;
    };

    const validateForm = () => {
        const newErrors = {};

        // Check fullName is not empty
        if (!draft.fullName || draft.fullName.trim() === '') {
            newErrors.fullName = 'Full name is required';
        }

        // Check phone is not empty
        if (!draft.phone || draft.phone.trim() === '') {
            newErrors.phone = 'Phone number is required';
        }

        // Check sex is selected
        if (!draft.sex || draft.sex.trim() === '') {
            newErrors.sex = 'Sex is required';
        }

        const dateOfBirthError = validateDateOfBirthValue(
            draft.dateOfBirth ? draft.dateOfBirth.substr(0, 10) : ''
        );
        if (dateOfBirthError) {
            newErrors.dateOfBirth = dateOfBirthError;
        }

        setErrors(newErrors);

        // Form is valid if there are no errors
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDraft({ ...draft, [name]: value });

        if (name === 'dateOfBirth') {
            const dateError = validateDateOfBirthValue(value);
            setErrors({ ...errors, dateOfBirth: dateError });
            return;
        }

        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSave(draft);
        }
    };

    return (
        <Box>
            <TextField
                label="Full Name"
                name="fullName"
                value={draft.fullName || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
                fullWidth
                error={!!errors.fullName}
                helperText={errors.fullName}
                required
            />

            <TextField
                label="Phone"
                name="phone"
                value={draft.phone || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone}
                required
            />

            <TextField
                label="Emergency Contact"
                name="emergencyContact"
                value={draft.emergencyContact || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
                fullWidth
            />

            <TextField
                label="Address"
                name="address"
                value={draft.address || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
                fullWidth
            />

            <TextField
                type="date"
                label="Date of Birth"
                name="dateOfBirth"
                value={draft.dateOfBirth ? draft.dateOfBirth.substr(0,10) : ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth}
                required
            />

            <TextField
                label="Sex"
                name="sex"
                select
                value={draft.sex || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
                fullWidth
                error={!!errors.sex}
                helperText={errors.sex}
                required
            >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
            </TextField>

            <Button
                variant="contained"
                onClick={handleSubmit}
            >
                Save
            </Button>

            <Button
                variant="outlined"
                color="error"
                onClick={onCancel}
                sx={{ ml: 2 }}
            >
                Cancel
            </Button>
        </Box>
    );
}